<template>
  <s-modal
    class="alarm-details-modal"
    v-model="showModal"
    size="xl"
    centered
    ok-only
    :title="titleHandler"
    :is-loading="loading"
    @show="refreshAlarmDetails()"
    @ok="closeAlarmDetailsModal()"
  >
    <b-container fluid>
      <b-row
        v-if="!loading"
        class="alarm-details-modal__table-wrapper"
        :key="currentPage"
      >
        <s-table
          :fields="fields"
          :items="items"
        >
          <template #cell(display)="data">
            <alarm-exhibition-column v-model="data.value" />
          </template>
          <template #cell(classification)="data">
            <div
              class="accordion-list__classification-badge d-inline-block"
              :style="classificationBadgeStyle"
            >
              <classification
                :classification="data.value"
              />
            </div>
          </template>
        </s-table>
      </b-row>
      <b-row
        v-else
        align-h="center"
      >
        <s-loader
          class="my-5"
          size="lg"
        />
      </b-row>
      <b-row>
        <grid-paginator
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="perPage"
          @input="paging"
        />
      </b-row>
    </b-container>
  </s-modal>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import AlarmDetailsService from '@/domain/services/generalPanel/adminPanel/alarmConfigurations';
import Classification from '@/components/Classification';
import LoadingMixin from '@/services/loadingMixin';
import AlarmExhibitionColumn from './AlarmExhibitionColumn';

export default {
  name: 'AlarmDetailsModal',
  mixins: [LoadingMixin],
  components: {
    GridPaginator,
    AlarmExhibitionColumn,
    Classification,
  },
  data: () => ({
    currentPage: 1,
    totalItems: null,
    perPage: 15,
    items: [],
  }),
  props: {
    value: {
      type: Boolean,
    },
    title: {
      type: String,
    },
    alarmId: {
      type: String,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    titleHandler() {
      const prefix = this.$t('admin-panel.alarm-details.title');
      return `${prefix} - ${this.title}`;
    },
    fields() {
      const fields = [
        {
          key: 'companyName',
          label: this.$t('entities.company'),
        },
        {
          key: 'regionalName',
          label: this.$t('entities.regional'),
        },
        {
          key: 'installationName',
          label: this.$t('entities.installation'),
        },
        {
          key: 'assetName',
          label: this.$t('entities.asset'),
        },
        {
          key: 'classification',
          label: this.$t('alarm-configurations.query.classification'),
          thClass: 'alarm-details-modal__table-classification text-center',
          tdClass: 'alarm-details-modal__table-classification text-center',
        },
      ];

      if (this.hasExhibitionPermission) {
        fields.splice(4, 0, {
          key: 'display',
          label: this.$t('alarm-configurations.query.exhibition'),
        });
      }

      return fields;
    },
    hasExhibitionPermission() {
      return this.$hasUserPermission('PER005');
    },
    classificationBadgeStyle() {
      const widths = {
        'manage-fonts_14px': '130px',
        'manage-fonts_16px': '150px',
        'manage-fonts_18px': '180px',
      };
      return {
        'max-width': widths[this.currentFontSize],
      };
    },
  },
  methods: {
    handleResponse(response = {}) {
      const { total, data } = response;
      this.totalItems = total;
      this.items = data;
    },
    refreshAlarmDetails() {
      if (!this.alarmId) return;
      const filter = {
        pageSize: this.perPage,
        page: this.currentPage,
        alarmId: this.alarmId,
        ...this.filter,
      };
      this.startLoading();
      AlarmDetailsService.getAlarmDetails(filter)
        .then(data => this.handleResponse(data))
        .finally(() => this.endLoading());
    },
    paging(page) {
      this.currentPage = page;
      this.refreshAlarmDetails();
    },
    closeAlarmDetailsModal() {
      this.showModal = false;
    },
  },
};
</script>
<style lang="sass" scoped>
  .alarm-details-modal
    ::v-deep &__table
      &-classification
        min-width: 145px
    &__table-wrapper
      max-height: 50vh
      overflow-y: auto
</style>
